import { useEffect, useMemo, useState } from 'react';

import AIJobProgressLoader, { FileAnalysisSubtitle } from 'components/ai/AIJobProgressLoader';
import SlideoutLoaderContainer from 'components/shared/ai/SlideoutLoaderContainer';
import JobbableAttachmentJobSubscriber from 'components/shared/ai/summary/JobbableAttachmentJobSubscriber';
import TimingExplainer from 'components/shared/ai/TimingExplainer';
import { useStageTimer } from 'hooks/useStageTimer';
import Admission from 'models/reviews/Admission';

type AdmissionLoadingStateProps = {
  admission: Admission;
  onLoadingFinished: () => void;
};

export default function AdmissionLoadingState({ admission, onLoadingFinished }: AdmissionLoadingStateProps) {
  const { attachments, summaryJobForCurrentAttachments: summaryJob, criteriaEvaluationJob: criteriaJob } = admission;

  const fileCount = attachments.length;

  const timerReferenceDate = summaryJob?.isAwaiting ? summaryJob.updatedAt : null;
  const onFinalStage = useStageTimer(timerReferenceDate, 60);

  const filesProcessing = attachments.some((a) => a.extractDataAIJob?.isProcessing);
  const criteriaDone = !criteriaJob || criteriaJob.isCompleted || criteriaJob.isErrored;
  const summaryDone = !summaryJob || summaryJob.isCompleted || summaryJob.isErrored;

  const stage = useMemo(() => {
    if (summaryDone && criteriaDone) return 5;
    if (onFinalStage) return 4;
    if (summaryJob?.isProcessing && criteriaDone) return 3;
    if (!filesProcessing && !criteriaDone) return 2;
    if (filesProcessing) return 1;
    return 0;
  }, [filesProcessing, criteriaDone, summaryJob, summaryDone, onFinalStage]);

  const count = useMemo(() => {
    return stage ? attachments.filter((a) => a.extractDataAIJob && !a.extractDataAIJob.isProcessing).length : 0;
  }, [stage, attachments]);

  const [completedCount, setCompletedCount] = useState(count);

  useEffect(() => {
    if (stage) {
      const timer = setTimeout(() => {
        setCompletedCount(count);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [stage, count]);

  const stages = useMemo(
    () => [
      {
        title: 'Processing files',
        subtitle: `${completedCount} of ${fileCount} complete`,
        subtasks: [
          { title: 'Scanning document text', annotation: `${completedCount}/${fileCount}` },
          { title: 'Extracting key information', annotation: `${completedCount}/${fileCount}` },
          { title: 'Creating source citations', annotation: `${completedCount}/${fileCount}` },
        ],
      },
      { title: 'Identifying admissions criteria' },
      { title: 'Formatting summary' },
      { title: 'Finishing up' },
    ],
    [completedCount, fileCount]
  );

  return (
    <SlideoutLoaderContainer>
      {attachments.map((attachment) => (
        <JobbableAttachmentJobSubscriber key={attachment.id} attachment={attachment} jobbable={admission} />
      ))}
      <AIJobProgressLoader
        title='Analyzing Files...'
        subtitle={<FileAnalysisSubtitle fileCount={fileCount} />}
        stages={stages}
        stage={stage}
        onLoadingFinished={onLoadingFinished}
      />
      <TimingExplainer jobType='summary' />
    </SlideoutLoaderContainer>
  );
}
